import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../config/api';

const UserContext = createContext(null);

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [favorites, setFavorites] = useState([]);

  // Check authentication on mount - only if we have indication user might be logged in
  useEffect(() => {
    // Check if there's any session indicator before making API call
    const hasSession = document.cookie.includes('session') || 
                       localStorage.getItem('user_session');
    if (hasSession) {
      checkAuth();
    } else {
      setLoading(false);
    }
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/users/me`, {
        withCredentials: true
      });
      setUser(response.data);
      setFavorites(response.data.favorites || []);
      // Store session indicator
      localStorage.setItem('user_session', 'true');
    } catch (error) {
      setUser(null);
      setFavorites([]);
      localStorage.removeItem('user_session');
    } finally {
      setLoading(false);
    }
  };

  // Register with email/password
  const register = async (email, password, name, newsletterAlerts = false) => {
    const response = await axios.post(`${API}/users/register`, {
      email,
      password,
      name,
      newsletter_alerts: newsletterAlerts
    }, { withCredentials: true });
    
    setUser(response.data);
    setFavorites(response.data.favorites || []);
    localStorage.setItem('user_session', 'true');
    return response.data;
  };

  // Login with email/password
  const login = async (email, password) => {
    const response = await axios.post(`${API}/users/login`, {
      email,
      password
    }, { withCredentials: true });
    
    setUser(response.data);
    setFavorites(response.data.favorites || []);
    localStorage.setItem('user_session', 'true');
    return response.data;
  };

  // Login with Google - Custom OAuth implementation
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const loginWithGoogle = async () => {
    try {
      // Build the callback URL using window.location.origin
      const callbackUrl = window.location.origin + '/auth/google/callback';
      
      // Get the Google auth URL from our backend
      const response = await axios.get(`${API}/auth/google/login`, {
        params: { redirect_uri: callbackUrl }
      });
      
      // Redirect to Google's consent screen
      window.location.href = response.data.auth_url;
    } catch (error) {
      console.error('Error initiating Google login:', error);
      throw error;
    }
  };

  // Process Google auth callback
  const processGoogleCallback = async (sessionId) => {
    const response = await axios.post(`${API}/users/google-auth`, {
      session_id: sessionId
    }, { withCredentials: true });
    
    setUser(response.data);
    setFavorites(response.data.favorites || []);
    return response.data;
  };

  // Logout
  const logout = async () => {
    try {
      await axios.post(`${API}/users/logout`, {}, { withCredentials: true });
    } catch (error) {
      // Ignore errors
    }
    setUser(null);
    setFavorites([]);
    localStorage.removeItem('user_session');
  };

  // Add to favorites
  const addFavorite = useCallback(async (varietyId) => {
    if (!user) return false;
    
    try {
      await axios.post(`${API}/users/favorites`, {
        variety_id: varietyId
      }, { withCredentials: true });
      
      setFavorites(prev => [...prev, varietyId]);
      return true;
    } catch (error) {
      console.error('Error adding favorite:', error);
      return false;
    }
  }, [user]);

  // Remove from favorites
  const removeFavorite = useCallback(async (varietyId) => {
    if (!user) return false;
    
    try {
      await axios.delete(`${API}/users/favorites/${varietyId}`, {
        withCredentials: true
      });
      
      setFavorites(prev => prev.filter(id => id !== varietyId));
      return true;
    } catch (error) {
      console.error('Error removing favorite:', error);
      return false;
    }
  }, [user]);

  // Toggle favorite
  const toggleFavorite = useCallback(async (varietyId) => {
    if (favorites.includes(varietyId)) {
      return removeFavorite(varietyId);
    } else {
      return addFavorite(varietyId);
    }
  }, [favorites, addFavorite, removeFavorite]);

  // Check if variety is favorite
  const isFavorite = useCallback((varietyId) => {
    return favorites.includes(varietyId);
  }, [favorites]);

  // Update newsletter preference
  const updateNewsletterAlerts = async (enabled) => {
    try {
      await axios.put(`${API}/users/newsletter-alerts?enabled=${enabled}`, {}, {
        withCredentials: true
      });
      setUser(prev => prev ? { ...prev, newsletter_alerts: enabled } : null);
      return true;
    } catch (error) {
      console.error('Error updating newsletter preference:', error);
      return false;
    }
  };

  // Add to view history
  const addToHistory = useCallback(async (varietyId) => {
    if (!user) return;
    
    try {
      await axios.post(`${API}/users/history/${varietyId}`, {}, {
        withCredentials: true
      });
    } catch (error) {
      // Silent fail - history is not critical
      console.error('Error adding to history:', error);
    }
  }, [user]);

  // Get view history
  const getHistory = useCallback(async (limit = 20) => {
    if (!user) return [];
    
    try {
      const response = await axios.get(`${API}/users/history?limit=${limit}`, {
        withCredentials: true
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching history:', error);
      return [];
    }
  }, [user]);

  // Clear history
  const clearHistory = useCallback(async () => {
    if (!user) return false;
    
    try {
      await axios.delete(`${API}/users/history`, {
        withCredentials: true
      });
      return true;
    } catch (error) {
      console.error('Error clearing history:', error);
      return false;
    }
  }, [user]);

  const value = {
    user,
    loading,
    isAuthenticated: !!user,
    favorites,
    register,
    login,
    loginWithGoogle,
    processGoogleCallback,
    logout,
    addFavorite,
    removeFavorite,
    toggleFavorite,
    isFavorite,
    updateNewsletterAlerts,
    addToHistory,
    getHistory,
    clearHistory,
    checkAuth
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};

export default UserContext;
